import { useCallback, useEffect, useRef, useState } from "react"
import { useRecordingState } from "../../contexts/RecordingStateContext"
import { shouldApplyUpdate } from "./helpers"
import { UpdatePromptView } from "./UpdatePromptView"

// Module-level so a React Strict Mode remount (dev) cannot post SKIP_WAITING
// twice or arm two fallback reload timers.
let applyStarted = false

/**
 * Applies a waiting service-worker update when this tab is not recording.
 *
 * Registration happens HERE (and only here): a plain
 * `navigator.serviceWorker.register("/sw.js")` below. We deliberately do not
 * use vite-plugin-pwa's `useRegisterSW` — besides its onNeedRefresh race
 * (the listener can attach after updatefound already fired), the hook also
 * attaches its own hidden `controlling → reload` listener that raced our
 * controllerchange handler in handleUpdate.
 *
 * Flow:
 *  1. On ready: attach updatefound listener, THEN call reg.update()
 *  2. updatefound → new SW installing → statechange to 'installed' → pending
 *  3. Also checks reg.waiting on ready (covers already-queued updates)
 *  4. Re-checks every 60s and on tab focus for long-lived sessions
 *  5. When pending AND not recording: skip-wait the worker we detected and
 *     reload on controllerchange (with a generous fallback timer that
 *     controllerchange cancels). A status pill shows while that runs.
 *
 * Workbox stays on registerType "prompt" — skipWaiting is still code-driven
 * so a new worker cannot take over mid-recording. The only thing that went
 * away is the click.
 */
export function UpdatePrompt(): React.ReactElement | null {
  const [pendingUpdate, setPendingUpdate] = useState(false)
  const { isRecordingActive } = useRecordingState()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const regRef = useRef<ServiceWorkerRegistration | null>(null)

  // Activate the waiting worker and reload. Posts SKIP_WAITING to the worker we
  // detected (the "prompt" SW build handles it → skipWaiting), then reloads once
  // the new SW takes control. A fallback reload covers browsers that don't fire
  // `controllerchange` reliably — controllerchange CANCELS it, and it is long
  // enough (10s) that activation (cleanupOutdatedCaches on a slow device) has
  // finished; reloading earlier would serve the OLD version and re-prompt.
  const handleUpdate = useCallback((): void => {
    if (applyStarted) return
    applyStarted = true
    const waiting = regRef.current?.waiting
    if (!waiting) {
      window.location.reload()
      return
    }
    const fallback = window.setTimeout(() => window.location.reload(), 10_000)
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        window.clearTimeout(fallback)
        window.location.reload()
      },
      { once: true },
    )
    waiting.postMessage({ type: "SKIP_WAITING" })
  }, [])

  useEffect(() => {
    if (!navigator.serviceWorker) return

    // Register the service worker. Production-only: in dev there is no
    // generated /sw.js (devOptions are off), and registering the SPA fallback
    // HTML as a worker just logs a MIME-type error.
    if (import.meta.env.PROD) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined)
    }

    navigator.serviceWorker.ready.then((reg) => {
      regRef.current = reg
      const triggerUpdate = (): void => setPendingUpdate(true)

      // Already has a waiting SW (e.g. update was found before this component
      // mounted) → mark pending now. Do NOT return: we must still attach the
      // updatefound listener + 60s polling below so that a SECOND update found
      // during this session is also detected. triggerUpdate() just sets a flag,
      // so calling it again from a later updatefound is idempotent.
      if (reg.waiting) {
        triggerUpdate()
      }

      // Attach updatefound BEFORE calling update() so we never miss the event
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing
        if (!sw) return
        sw.addEventListener("statechange", () => {
          // 'installed' + existing controller = new SW waiting to take over
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            triggerUpdate()
          }
        })
      })

      const check = (): void => {
        reg.update().catch(() => undefined)
      }

      // Immediate check now that the listener is in place
      check()

      // Periodic check for long-lived sessions
      intervalRef.current = setInterval(check, 60_000)
    })

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        navigator.serviceWorker?.ready.then((reg) => reg.update()).catch(() => undefined)
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    if (!shouldApplyUpdate(pendingUpdate, isRecordingActive)) return
    handleUpdate()
  }, [pendingUpdate, isRecordingActive, handleUpdate])

  if (!pendingUpdate || isRecordingActive) return null

  return <UpdatePromptView />
}
